/**
 * FAQ responses for the HelpChat component.
 *
 * Uses keyword matching to find relevant responses.
 * Supports both English and Hebrew.
 *
 * Epic 6 Note: This FAQ system may be upgraded to AI-powered chat
 * when the marketing site is implemented. The interface is designed
 * to allow swapping the implementation without changing the HelpChat component.
 */

import type { FaqResponses } from './types';

export const faqResponsesEn: FaqResponses = {
  howItWorks: {
    keywords: ['how', 'work', 'does it', 'what is', 'explain'],
    response:
      "It's simple! Just upload your content (files, text, or links) and Boty automatically creates an AI assistant. Share a link, and anyone can ask questions 24/7.",
  },
  cost: {
    keywords: ['cost', 'price', 'pay', 'free', 'money', 'subscription'],
    response:
      'Boty is completely free! Create unlimited bots without signing up or paying anything. No credit card needed, no hidden fees.',
  },
  time: {
    keywords: ['time', 'long', 'fast', 'minute', 'quick', 'speed'],
    response:
      'Under 2 minutes! Just upload your content and your bot is ready to answer questions immediately.',
  },
  sharing: {
    keywords: ['share', 'distribute', 'link', 'send', 'whatsapp', 'email'],
    response:
      'Once your bot is ready, you get a shareable link. Send it via WhatsApp, email, or embed it on your website.',
  },
  editing: {
    keywords: ['edit', 'update', 'change', 'modify', 'add more'],
    response:
      "Yes! You can update your bot's content anytime. Just add new files or text, and your bot learns instantly.",
  },
  analytics: {
    keywords: ['analytics', 'track', 'stats', 'question', 'insight', 'data'],
    response:
      'Boty shows you what questions people ask most, helping you understand what matters to your audience.',
  },
  creator: {
    keywords: ['who', 'created', 'made', 'built', 'develop', 'founder', 'team', 'behind'],
    response:
      'Mainly bots created Boty, with some help from Yoni Meidan and the team at Interneteams.',
  },
  fallback: {
    response:
      "Great question! Boty lets you create free AI assistants that answer questions about your content 24/7. Try creating one now — completely free, no credit card needed!",
  },
};

export const faqResponsesHe: FaqResponses = {
  howItWorks: {
    keywords: ['איך', 'עובד', 'מה זה', 'הסבר', 'פועל'],
    response:
      'זה פשוט! העלו את התוכן שלכם (קבצים, טקסט או קישורים) ובוטי יוצר אוטומטית עוזר AI. שתפו קישור, וכל אחד יכול לשאול שאלות 24/7.',
  },
  cost: {
    keywords: ['עלות', 'מחיר', 'תשלום', 'חינם', 'כסף', 'מנוי'],
    response:
      'בוטי חינמי לגמרי! צרו בוטים ללא הגבלה בלי להירשם ובלי לשלם כלום. ללא כרטיס אשראי, ללא עלויות נסתרות.',
  },
  time: {
    keywords: ['זמן', 'כמה', 'מהיר', 'דקות', 'מהירות'],
    response:
      'פחות מ-2 דקות! העלו את התוכן והבוט מוכן לענות על שאלות מיד.',
  },
  sharing: {
    keywords: ['שיתוף', 'קישור', 'לשלוח', 'וואטסאפ', 'אימייל'],
    response:
      'כשהבוט מוכן, מקבלים קישור לשיתוף. שלחו בוואטסאפ, אימייל, או הטמיעו באתר.',
  },
  editing: {
    keywords: ['עריכה', 'עדכון', 'שינוי', 'להוסיף', 'לערוך'],
    response:
      'כן! אפשר לעדכן את התוכן של הבוט בכל זמן. הוסיפו קבצים או טקסט והבוט לומד מיד.',
  },
  analytics: {
    keywords: ['נתונים', 'סטטיסטיקה', 'שאלות', 'תובנות', 'מעקב'],
    response:
      'בוטי מראה אילו שאלות אנשים שואלים הכי הרבה, כך שתבינו מה חשוב לקהל שלכם.',
  },
  creator: {
    keywords: ['מי', 'יצר', 'בנה', 'פיתח', 'מייסד', 'צוות', 'מאחורי'],
    response:
      'בעיקר בוטים יצרו את בוטי, עם קצת עזרה מיוני מידן והצוות של Interneteams.',
  },
  fallback: {
    response:
      'שאלה מעולה! בוטי מאפשר ליצור עוזרי AI חינמיים שעונים על שאלות לגבי התוכן שלכם 24/7. נסו ליצור אחד עכשיו — חינמי לגמרי, ללא כרטיס אשראי!',
  },
};

/**
 * Match a question to an FAQ response based on keywords.
 *
 * @param question - The user's question
 * @param locale - The current locale ('en' or 'he')
 * @returns The matched FAQ response or fallback
 */
export function matchFaq(question: string, locale: string): string {
  const q = question.toLowerCase();
  const responses = locale === 'he' ? faqResponsesHe : faqResponsesEn;

  for (const [key, faq] of Object.entries(responses)) {
    if (key === 'fallback') continue;
    const entry = faq as { keywords: string[]; response: string };
    if (entry.keywords?.some((kw) => q.includes(kw))) {
      return entry.response;
    }
  }

  return responses.fallback.response;
}
